import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from './api';
import {
  daysAgo,
  formatNumber,
  formatRelative,
  setDisplayZone,
  setZoneRules,
  toDateInput,
  zoneAbbreviation,
  zoneForLocations,
} from './format';
import { ZONE_STORAGE_KEY } from './zoneBoot';
import type {
  AppointmentFilterState,
  OpportunitiesResponse,
  OpportunityFilterState,
  OpportunitySummaryResponse,
  AppointmentSummaryResponse,
  AppointmentsResponse,
  AuthStatus,
  CallFilterState,
  CallsResponse,
  CallerQaResponse,
  LocationSummary,
  MessageFilterState,
  OverviewFilterState,
  SummaryResponse,
  SyncStatus,
  TabId,
  TabLevel,
} from './types';
import { FilterBar } from './components/FilterBar';
import { AppointmentFilterBar } from './components/AppointmentFilterBar';
import { OpportunityFilterBar } from './components/OpportunityFilterBar';
import { DEFAULT_OVERVIEW_FILTERS, Overview } from './pages/Overview';
import { CallsOverview } from './pages/CallsOverview';
import { DEFAULT_MESSAGE_FILTERS, Messages } from './pages/Messages';
import { Calls } from './pages/Calls';
import { Appointments } from './pages/Appointments';
import { Opportunities } from './pages/Opportunities';
import { Subaccounts } from './pages/Subaccounts';
import { SyncStatusPage } from './pages/SyncStatusPage';
import { Connect } from './pages/Connect';
import { Login } from './pages/Login';
import { Files } from './pages/Files';
import { Logs } from './pages/Logs';
import { Settings } from './pages/Settings';
import { Assistant } from './pages/Assistant';
import { SavedViews } from './components/SavedViews';
import { LocationSummaryPanel } from './components/AiSummary';

type Tab = TabId;

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'calls', label: 'Calls' },
  { id: 'callLog', label: 'Call log' },
  { id: 'messages', label: 'Messages' },
  { id: 'appointments', label: 'Appointments' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'assistant', label: 'Assistant' },
  { id: 'subaccounts', label: 'Sub-accounts' },
  { id: 'files', label: 'Files' },
  { id: 'logs', label: 'Logs' },
  { id: 'sync', label: 'Sync' },
  { id: 'settings', label: 'Settings' },
];

const DEFAULT_FILTERS: CallFilterState = {
  from: daysAgo(29),
  to: toDateInput(new Date()),
  locationIds: [],
  userIds: [],
  outcomes: [],
  direction: '',
  search: '',
  // Default to live clients: a churned sub-account's history should not quietly
  // move the agency's totals.
  activeClientsOnly: true,
  tags: [],
  tagMatch: 'any',
};

// Appointments default to a window that straddles today, because they are
// booked into the future as well as sitting in the past.
const DEFAULT_APPOINTMENT_FILTERS: AppointmentFilterState = {
  from: daysAgo(29),
  to: toDateInput(new Date(Date.now() + 30 * 86_400_000)),
  locationIds: [],
  userIds: [],
  calendarIds: [],
  outcomes: [],
  search: '',
  activeClientsOnly: true,
  tags: [],
  tagMatch: 'any',
  dateBasis: 'start',
  userBasis: 'booker',
};

// Opportunities run on a longer horizon than calls: a deal created three months
// ago is still the one being reported on.
const DEFAULT_OPPORTUNITY_FILTERS: OpportunityFilterState = {
  from: daysAgo(89),
  to: toDateInput(new Date()),
  locationIds: [],
  userIds: [],
  statuses: [],
  pipelineIds: [],
  stageIds: [],
  search: '',
  activeClientsOnly: true,
  tags: [],
  tagMatch: 'any',
  includeDeleted: false,
  appointmentFrom: '',
  appointmentTo: '',
  appointmentState: 'any',
  sort: 'created',
  minLeadScore: '',
};

type Theme = 'system' | 'light' | 'dark';

function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('ghl-dashboard-theme') as Theme | null) ?? 'system',
  );

  useEffect(() => {
    if (theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ghl-dashboard-theme', theme);
  }, [theme]);

  return [theme, setTheme];
}

/** Debounces the filter state so typing in search doesn't fire a request per keystroke. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function App() {
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [theme, setTheme] = useTheme();

  const [overviewFilters, setOverviewFilters] = useState<OverviewFilterState>(DEFAULT_OVERVIEW_FILTERS);
  const [messageFilters, setMessageFilters] = useState<MessageFilterState>(DEFAULT_MESSAGE_FILTERS);

  const [filters, setFilters] = useState<CallFilterState>(DEFAULT_FILTERS);
  const debouncedFilters = useDebounced(filters, 250);

  const [apptFilters, setApptFilters] = useState<AppointmentFilterState>(DEFAULT_APPOINTMENT_FILTERS);
  const debouncedApptFilters = useDebounced(apptFilters, 250);

  const [oppFilters, setOppFilters] = useState<OpportunityFilterState>(DEFAULT_OPPORTUNITY_FILTERS);
  const debouncedOppFilters = useDebounced(oppFilters, 250);

  const [locations, setLocations] = useState<LocationSummary[]>([]);
  // Reports wait for the sub-account list: a single selected sub-account may set the time zone.
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [callers, setCallers] = useState<
    { userId: string | null; userName: string | null; locationId: string; total: number }[]
  >([]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [callerQa, setCallerQa] = useState<CallerQaResponse | null>(null);
  /** Bumped after a call is reviewed, so the call log shows the new score. */
  const [callsReload, setCallsReload] = useState(0);
  const [calls, setCalls] = useState<CallsResponse | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  /**
   * Bumped when the server reports new data, so every open report re-fetches
   * by itself. `quietRefresh` keeps those refreshes from dimming the page the
   * way a filter change does — the numbers just update.
   */
  const [dataVersion, setDataVersion] = useState(0);
  const quietRefresh = useRef(false);

  const [apptSummary, setApptSummary] = useState<AppointmentSummaryResponse | null>(null);
  const [apptList, setApptList] = useState<AppointmentsResponse | null>(null);
  const [apptPeople, setApptPeople] = useState<
    { userId: string | null; userName: string | null; locationId: string; total: number }[]
  >([]);
  const [apptCalendars, setApptCalendars] = useState<
    { id: string; name: string | null; locationId: string }[]
  >([]);
  const [apptPage, setApptPage] = useState(1);
  const [apptPageSize, setApptPageSize] = useState(50);

  const [oppSummary, setOppSummary] = useState<OpportunitySummaryResponse | null>(null);
  const [oppList, setOppList] = useState<OpportunitiesResponse | null>(null);
  const [oppPeople, setOppPeople] = useState<
    { userId: string | null; userName: string | null; locationId: string; total: number }[]
  >([]);
  const [pipelines, setPipelines] = useState<{ id: string; name: string | null; locationId: string }[]>([]);
  const [stages, setStages] = useState<
    { id: string; name: string | null; pipelineId: string; position: number }[]
  >([]);
  const [oppPage, setOppPage] = useState(1);
  const [oppPageSize, setOppPageSize] = useState(50);

  // Tags are shared by every report's filter bar.
  const [tagOptions, setTagOptions] = useState<{ tag: string; contacts: number }[]>([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshAuth = useCallback(() => {
    api
      .authStatus()
      .then(setAuth)
      .catch((cause) => setError(String(cause)));
  }, []);

  useEffect(refreshAuth, [refreshAuth]);

  const ready = auth?.signedIn && auth.connected;
  /** This person's level on a tab; without named users everyone is the owner. */
  const level = useCallback(
    (id: Tab): TabLevel => (auth?.user?.tabs ? auth.user.tabs[id] ?? 'off' : 'edit'),
    [auth],
  );
  const canSeeCalls = level('calls') !== 'off' || level('callLog') !== 'off';

  // Each sub-account's own zone, and the agency's settings, for the zone rules.
  const locationZones = useMemo(
    () => new Map(locations.filter((location) => location.timezone).map((location) => [location.id, location.timezone!])),
    [locations],
  );
  const timeZones = auth?.timeZones;
  useEffect(() => {
    if (!timeZones) return;
    try {
      localStorage.setItem(ZONE_STORAGE_KEY, JSON.stringify(timeZones));
    } catch {
      /* storage blocked: the settings still apply from the server each visit */
    }
  }, [timeZones]);
  setZoneRules({
    defaultZone: timeZones?.defaultZone || null,
    followSubaccount: timeZones?.followSubaccount ?? true,
    byLocation: locationZones,
  });

  // A tab someone has no access to falls back to the first one they have.
  useEffect(() => {
    if (level(tab) !== 'off') return;
    const first = TABS.find((item) => level(item.id) !== 'off');
    if (first) setTab(first.id);
  }, [level, tab]);

  const refreshLocations = useCallback(() => {
    if (!ready) return;
    api
      .locations()
      .then((response) => {
        setLocations(response.locations);
        setLocationsLoaded(true);
      })
      .catch((cause) => setError(String(cause)));
  }, [ready]);

  const refreshSync = useCallback(() => {
    if (!ready) return;
    api.syncStatus().then(setSyncStatus).catch(() => undefined);
  }, [ready]);

  useEffect(refreshLocations, [refreshLocations]);
  useEffect(refreshSync, [refreshSync]);

  // Poll quickly while a sync or enrichment is in flight, slowly otherwise, so
  // the scheduler's own background syncs reach the screen without a reload.
  const busy = Boolean(syncStatus?.running || syncStatus?.enriching);
  useEffect(() => {
    if (!ready) return;
    const timer = setTimeout(refreshSync, busy ? 4000 : 30_000);
    return () => clearTimeout(timer);
  }, [ready, busy, syncStatus, refreshSync]);

  // New data on the server: refresh what is on screen.
  const serverDataVersion = syncStatus?.dataVersion;
  useEffect(() => {
    if (serverDataVersion === undefined) return;
    setDataVersion((current) => {
      if (current === serverDataVersion) return current;
      quietRefresh.current = true;
      return serverDataVersion;
    });
  }, [serverDataVersion]);
  useEffect(() => {
    if (dataVersion > 0) refreshLocations();
  }, [dataVersion, refreshLocations]);

  // Reports re-fetch whenever the shared filter slice changes.
  useEffect(() => {
    if (!ready || !canSeeCalls || !locationsLoaded) return;
    let cancelled = false;
    if (!quietRefresh.current) setLoading(true);
    Promise.all([api.summary(debouncedFilters), api.callers(debouncedFilters)])
      .then(([summaryResponse, callersResponse]) => {
        if (cancelled) return;
        setSummary(summaryResponse);
        setCallers(callersResponse.callers);
        setError(null);
      })
      .catch((cause) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 401) refreshAuth();
        else setError(cause instanceof ApiError ? cause.message : String(cause));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ready, canSeeCalls, locationsLoaded, debouncedFilters, refreshAuth, dataVersion]);

  // The team's call QA scorecard, on the Calls tab.
  useEffect(() => {
    if (!ready || tab !== 'calls' || !locationsLoaded) return;
    let cancelled = false;
    api
      .callerQa(debouncedFilters)
      .then((response) => !cancelled && setCallerQa(response))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [ready, tab, locationsLoaded, debouncedFilters, dataVersion]);

  useEffect(() => setPage(1), [debouncedFilters]);
  useEffect(() => setApptPage(1), [debouncedApptFilters]);
  useEffect(() => setOppPage(1), [debouncedOppFilters]);

  // The tag list follows the sub-account and active-client scope, so it only
  // ever offers tags that exist inside the current slice.
  useEffect(() => {
    if (!ready || !auth?.user || TABS.every((item) => item.id === 'overview' || level(item.id) === 'off')) return;
    api
      .tags(debouncedFilters)
      .then((response) => setTagOptions(response.tags))
      .catch(() => undefined);
  }, [ready, auth, level, debouncedFilters.locationIds, debouncedFilters.activeClientsOnly, debouncedFilters]);

  useEffect(() => {
    if (!ready || tab !== 'opportunities' || !locationsLoaded) return;
    let cancelled = false;
    if (!quietRefresh.current) setLoading(true);
    Promise.all([
      api.opportunitySummary(debouncedOppFilters),
      api.opportunityPeople(debouncedOppFilters),
      api.opportunityPipelines(debouncedOppFilters),
      api.opportunities(debouncedOppFilters, oppPage, oppPageSize),
    ])
      .then(([summaryResponse, peopleResponse, pipelineResponse, listResponse]) => {
        if (cancelled) return;
        setOppSummary(summaryResponse);
        setOppPeople(peopleResponse.people);
        setPipelines(pipelineResponse.pipelines);
        setStages(pipelineResponse.stages);
        setOppList(listResponse);
        setError(null);
      })
      .catch((cause) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 401) refreshAuth();
        else setError(cause instanceof ApiError ? cause.message : String(cause));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ready, tab, locationsLoaded, debouncedOppFilters, oppPage, oppPageSize, refreshAuth, dataVersion]);

  useEffect(() => {
    if (!ready || tab !== 'appointments' || !locationsLoaded) return;
    let cancelled = false;
    if (!quietRefresh.current) setLoading(true);
    Promise.all([
      api.appointmentSummary(debouncedApptFilters),
      api.appointmentPeople(debouncedApptFilters),
      api.appointmentCalendars(debouncedApptFilters),
      api.appointments(debouncedApptFilters, apptPage, apptPageSize),
    ])
      .then(([summaryResponse, peopleResponse, calendarResponse, listResponse]) => {
        if (cancelled) return;
        setApptSummary(summaryResponse);
        setApptPeople(peopleResponse.people);
        setApptCalendars(calendarResponse.calendars);
        setApptList(listResponse);
        setError(null);
      })
      .catch((cause) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 401) refreshAuth();
        else setError(cause instanceof ApiError ? cause.message : String(cause));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ready, tab, locationsLoaded, debouncedApptFilters, apptPage, apptPageSize, refreshAuth, dataVersion]);

  useEffect(() => {
    if (!ready || tab !== 'callLog' || !locationsLoaded) return;
    let cancelled = false;
    if (!quietRefresh.current) setLoading(true);
    api
      .calls(debouncedFilters, page, pageSize)
      .then((response) => !cancelled && setCalls(response))
      .catch((cause) => !cancelled && setError(cause instanceof ApiError ? cause.message : String(cause)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ready, tab, locationsLoaded, debouncedFilters, page, pageSize, dataVersion, callsReload]);

  // Declared after every data effect, so it runs after them in the same commit.
  useEffect(() => {
    quietRefresh.current = false;
  }, [dataVersion]);

  const trackedCount = useMemo(
    () => locations.filter((location) => location.trackingEnabled).length,
    [locations],
  );

  async function runSync() {
    await api.runSync();
    setSyncStatus((current) => (current ? { ...current, running: true } : current));
    setTimeout(refreshSync, 1500);
  }

  if (!auth) {
    return <div className="centered muted">Loading…</div>;
  }
  if (!auth.signedIn) {
    return <Login onSignedIn={refreshAuth} namedUsers={auth.namedUsers} />;
  }
  if (!auth.connected) {
    return <Connect onConnected={refreshAuth} oauthConfigured={auth.oauthConfigured} />;
  }

  const visibleTabs = TABS.filter((item) => level(item.id) !== 'off');

  // Time zone for everything below: set before the pages render, so their
  // dates and times — and the requests they make — all use the same one.
  const tabLocations =
    tab === 'overview'
      ? overviewFilters.locationIds
      : tab === 'messages'
        ? messageFilters.locationIds
        : tab === 'appointments'
        ? apptFilters.locationIds
        : tab === 'opportunities'
          ? oppFilters.locationIds
          : tab === 'calls' || tab === 'callLog'
            ? filters.locationIds
            : [];
  const zone = zoneForLocations(tabLocations);
  setDisplayZone(zone);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">RD</span>
          <span>
            <div className="brand-name">Reporting Dashboard</div>
            <div className="brand-sub">HighLevel agency</div>
          </span>
        </div>

        <nav className="nav">
          {visibleTabs.map((item) => (
            <button
              type="button"
              key={item.id}
              aria-current={tab === item.id ? 'page' : undefined}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              {item.id === 'subaccounts' ? <span className="count">{formatNumber(trackedCount)}</span> : null}
              {item.id === 'callLog' && summary ? (
                <span className="count">{formatNumber(summary.totals.calls)}</span>
              ) : null}
              {item.id === 'appointments' && apptSummary ? (
                <span className="count">{formatNumber(apptSummary.totals.appointments)}</span>
              ) : null}
              {item.id === 'opportunities' && oppSummary ? (
                <span className="count">{formatNumber(oppSummary.totals.opportunities)}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="field">
            <label htmlFor="theme">Theme</label>
            <select id="theme" value={theme} onChange={(event) => setTheme(event.target.value as Theme)}>
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
          <div>Synced {formatRelative(syncStatus?.lastSummary?.finishedAt ?? null)}</div>
          {auth.user ? (
            <div className="signed-in">
              <span className="cell-strong">{auth.user.name}</span>
              <span className="role-chip">{auth.user.role}</span>
            </div>
          ) : null}
          {auth.passwordRequired ? (
            <button type="button" className="btn btn-sm" onClick={() => api.logout().then(refreshAuth)}>
              Sign out
            </button>
          ) : null}
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <h1>{TABS.find((item) => item.id === tab)?.label}</h1>
          <span className="small muted zone-hint" title={`Dates and times on this page are in ${zone}`}>
            Times in {zone.replace(/_/g, ' ')} ({zoneAbbreviation(zone)})
          </span>
          <div className="topbar-actions">
            {syncStatus?.running ? <span className="small muted">Syncing…</span> : null}
            {auth.demoMode || level('sync') !== 'edit' ? null : (
              <button type="button" className="btn" onClick={runSync} disabled={syncStatus?.running}>
                Sync now
              </button>
            )}
          </div>
        </header>

        <div className="content">
          {auth.demoMode ? (
            <div className="banner">
              <span>
                <strong>Demo data.</strong> These numbers are invented — nothing is connected to
                HighLevel and syncing is off. Remove <code>DEMO_MODE</code> from <code>.env</code>,
                delete the <code>data/</code> folder, and restart to connect your agency.
              </span>
            </div>
          ) : null}

          {error ? (
            <div className="banner error">
              <span>{error}</span>
              <button type="button" className="btn btn-sm" onClick={() => setError(null)}>
                Dismiss
              </button>
            </div>
          ) : null}

          {visibleTabs.length === 0 ? (
            <p className="empty">Your account has no tabs turned on. Ask an owner to give you access under Settings → Users.</p>
          ) : null}

          {tab === 'calls' || tab === 'callLog' ? (
            <FilterBar
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(DEFAULT_FILTERS)}
              views={<SavedViews page="calls" filters={filters} defaults={DEFAULT_FILTERS} onApply={setFilters} />}
              locations={locations}
              callers={callers}
              showCallerFilter
              showSearch={tab === 'callLog'}
              tagOptions={tagOptions}
            />
          ) : null}

          {tab === 'appointments' ? (
            <AppointmentFilterBar
              filters={apptFilters}
              onChange={setApptFilters}
              onReset={() => setApptFilters(DEFAULT_APPOINTMENT_FILTERS)}
              views={
                <SavedViews
                  page="appointments"
                  filters={apptFilters}
                  defaults={DEFAULT_APPOINTMENT_FILTERS}
                  onApply={setApptFilters}
                />
              }
              locations={locations}
              people={apptPeople}
              calendars={apptCalendars}
              tagOptions={tagOptions}
              showSearch
            />
          ) : null}

          {tab === 'overview' && locationsLoaded ? (
            <Overview
              filters={overviewFilters}
              onFiltersChange={setOverviewFilters}
              locations={locations}
              dataVersion={dataVersion}
              showAi={!auth.demoMode}
              canEditNotes={level('overview') === 'edit'}
              onOpen={(report, locationId) => {
                const scope = { from: overviewFilters.from, to: overviewFilters.to, activeClientsOnly: overviewFilters.activeClientsOnly };
                const locationIds = locationId ? [locationId] : [];
                if (report === 'calls') setFilters((f) => ({ ...f, ...scope, locationIds, userIds: [] }));
                if (report === 'messages') setMessageFilters((f) => ({ ...f, ...scope, locationIds, userIds: [] }));
                if (report === 'appointments') setApptFilters((f) => ({ ...f, ...scope, locationIds, userIds: [] }));
                if (report === 'opportunities') setOppFilters((f) => ({ ...f, ...scope, locationIds, userIds: [] }));
                setTab(report === 'calls' && level('calls') === 'off' ? 'callLog' : report);
              }}
            />
          ) : null}

          {tab === 'calls' && debouncedFilters.locationIds.length === 1 && !auth.demoMode ? (
            <LocationSummaryPanel
              locationId={debouncedFilters.locationIds[0]!}
              from={debouncedFilters.from}
              to={debouncedFilters.to}
            />
          ) : null}

          {tab === 'calls' ? (
            <CallsOverview
              summary={summary}
              loading={loading}
              locations={locations}
              filters={filters}
              onFiltersChange={setFilters}
              onOpenCalls={() => setTab('callLog')}
              qa={callerQa}
            />
          ) : null}

          {tab === 'messages' && locationsLoaded ? (
            <Messages
              filters={messageFilters}
              onFiltersChange={setMessageFilters}
              locations={locations}
              tagOptions={tagOptions}
              dataVersion={dataVersion}
            />
          ) : null}

          {tab === 'callLog' ? (
            <Calls
              data={calls}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              filters={debouncedFilters}
              canReview={level('callLog') === 'edit' && !auth.demoMode}
              onReviewed={() => setCallsReload((current) => current + 1)}
            />
          ) : null}

          {tab === 'opportunities' ? (
            <OpportunityFilterBar
              filters={oppFilters}
              onChange={setOppFilters}
              onReset={() => setOppFilters(DEFAULT_OPPORTUNITY_FILTERS)}
              views={
                <SavedViews
                  page="opportunities"
                  filters={oppFilters}
                  defaults={DEFAULT_OPPORTUNITY_FILTERS}
                  onApply={setOppFilters}
                />
              }
              locations={locations}
              people={oppPeople}
              pipelines={pipelines}
              stages={stages}
              tagOptions={tagOptions}
            />
          ) : null}

          {tab === 'opportunities' ? (
            <Opportunities
              summary={oppSummary}
              list={oppList}
              loading={loading}
              locations={locations}
              filters={oppFilters}
              onFiltersChange={setOppFilters}
              page={oppPage}
              pageSize={oppPageSize}
              onPageChange={setOppPage}
              onPageSizeChange={(size) => {
                setOppPageSize(size);
                setOppPage(1);
              }}
            />
          ) : null}

          {tab === 'appointments' ? (
            <Appointments
              summary={apptSummary}
              list={apptList}
              loading={loading}
              locations={locations}
              filters={apptFilters}
              onFiltersChange={setApptFilters}
              page={apptPage}
              pageSize={apptPageSize}
              onPageChange={setApptPage}
              onPageSizeChange={(size) => {
                setApptPageSize(size);
                setApptPage(1);
              }}
            />
          ) : null}

          {tab === 'subaccounts' ? (
            <Subaccounts
              locations={locations}
              canCreate={auth.hasAgencyCredentials}
              canEdit={level('subaccounts') === 'edit'}
              canOpenSettings={level('settings') === 'edit'}
              onOpenSettings={() => setTab('settings')}
              onRefresh={() => {
                refreshLocations();
                refreshSync();
              }}
              onConnectionsChanged={() => {
                refreshAuth();
                refreshLocations();
                refreshSync();
              }}
            />
          ) : null}

          {tab === 'files' ? <Files locations={locations} canEdit={level('files') === 'edit'} /> : null}

          {tab === 'logs' ? <Logs locations={locations} /> : null}

          {tab === 'settings' && level('settings') !== 'off' ? (
            <Settings
              canManageUsers={auth.user?.canManageUsers ?? true}
              locations={locations}
              timeZones={auth.timeZones}
              onTimeZonesSaved={() => {
                // Every date range and chart is worked out again in the new zone.
                window.location.reload();
              }}
            />
          ) : null}

          {tab === 'assistant' ? <Assistant locations={locations} user={auth.user} /> : null}

          {tab === 'sync' ? (
            <SyncStatusPage status={syncStatus} onRunAll={runSync} canRun={level('sync') === 'edit'} />
          ) : null}
        </div>
      </main>
    </div>
  );
}
